import {
  type EdgelessToolbarI18nKey,
  edgelessToolbarI18nT,
} from '@blocksuite/affine-shared/services';
import { stopPropagation } from '@blocksuite/affine-shared/utils';
import { css, html, nothing, type TemplateResult } from 'lit';

export type ExternalEmbedProvider =
  | 'youtube'
  | 'figma'
  | 'loom'
  | 'iframe'
  | 'html';

export interface ExternalEmbedGateOptions {
  provider: ExternalEmbedProvider;
  /** Already proxied by the caller (ImageProxyService.buildUrl), or null. */
  previewImageUrl: string | null;
  /** Where the content comes from (e.g. the website's host), shown under the notice. */
  source?: string;
  onLoad: () => void;
}

const NOTICE_KEYS: Record<ExternalEmbedProvider, EdgelessToolbarI18nKey> = {
  youtube: 'embed.externalContent.noticeYoutube',
  figma: 'embed.externalContent.noticeFigma',
  loom: 'embed.externalContent.noticeLoom',
  iframe: 'embed.externalContent.noticeIframe',
  html: 'embed.externalContent.noticeHtml',
};

/** Blocks that render the gate add these styles to their own. */
export const externalEmbedGateStyles = css`
  .affine-external-embed-gate {
    position: relative;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 12px;
    width: 100%;
    height: 100%;
    padding: 16px;
    box-sizing: border-box;
    overflow: hidden;
    border-radius: 4px;
    background: var(--affine-background-secondary-color);
    color: var(--affine-text-primary-color);
    text-align: center;
  }

  .affine-external-embed-gate-preview {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
    opacity: 0.35;
    pointer-events: none;
  }

  .affine-external-embed-gate-notice,
  .affine-external-embed-gate-source,
  .affine-external-embed-gate-load {
    position: relative;
    font-size: var(--affine-font-sm);
    line-height: 20px;
  }

  .affine-external-embed-gate-source {
    color: var(--affine-text-secondary-color);
    word-break: break-all;
  }

  .affine-external-embed-gate-load {
    padding: 4px 12px;
    border: 1px solid var(--affine-border-color);
    border-radius: 8px;
    background: var(--affine-white);
    color: var(--affine-text-primary-color);
    cursor: pointer;
  }

  .affine-external-embed-gate-load:focus-visible {
    outline: 2px solid var(--affine-primary-color);
    outline-offset: 2px;
  }
`;

/**
 * Preview image routed through the image proxy, so the provider is not
 * contacted before the reader loads the content.
 */
export function proxiedPreviewUrl(
  imageProxy: { buildUrl(imageUrl: string): string },
  image: string | null | undefined
): string | null {
  return image ? imageProxy.buildUrl(image) : null;
}

/**
 * Placeholder for third-party content: nothing is fetched from the provider
 * until the reader presses the button. The release lives only in the caller's
 * component state for this view.
 */
export function renderExternalEmbedGate({
  provider,
  previewImageUrl,
  source,
  onLoad,
}: ExternalEmbedGateOptions): TemplateResult {
  const load = (event: Event) => {
    event.stopPropagation();
    onLoad();
  };
  const onKeydown = (event: KeyboardEvent) => {
    event.stopPropagation();
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onLoad();
    }
  };

  return html`
    <div class="affine-external-embed-gate" data-provider=${provider}>
      ${previewImageUrl
        ? html`<img
            class="affine-external-embed-gate-preview"
            src=${previewImageUrl}
            alt=""
          />`
        : nothing}
      <div class="affine-external-embed-gate-notice">
        ${edgelessToolbarI18nT(NOTICE_KEYS[provider])}
      </div>
      ${source
        ? html`<div class="affine-external-embed-gate-source">${source}</div>`
        : nothing}
      <button
        type="button"
        class="affine-external-embed-gate-load"
        @pointerdown=${stopPropagation}
        @pointerup=${stopPropagation}
        @click=${load}
        @dblclick=${stopPropagation}
        @keydown=${onKeydown}
      >
        ${edgelessToolbarI18nT('embed.externalContent.load')}
      </button>
    </div>
  `;
}
